'use client'

import type {
  GlobalErrorPageProps,
  UseGlobalErrorPageReturnType,
} from '@/components/global-error-page/global-error-page.types'
import type { AbstractIntlMessages } from 'next-intl'

import { usePathname } from 'next/navigation'
import { useEffect, useState, useSyncExternalStore } from 'react'

import {
  getClientErrorTitle,
  loadGlobalErrorMessages,
} from '@/components/global-error-page/global-error-messages'
import {
  reportGlobalError,
  resolveClientLocale,
} from '@/components/global-error-page/global-error-page.utils'
import { theme } from '@maestro/local/theme'

const subscribe = () => () => {}
const getServerSnapshot = () => theme().site.locale

export function useGlobalErrorPage(
  error: GlobalErrorPageProps['error']
): UseGlobalErrorPageReturnType {
  const pathname = usePathname()
  const locale = useSyncExternalStore(
    subscribe,
    resolveClientLocale,
    getServerSnapshot
  )
  const [messages, setMessages] = useState<AbstractIntlMessages | null>(null)

  useEffect(() => {
    let cancelled = false

    loadGlobalErrorMessages(locale)
      // Site default as fallback: every supported locale has message files.
      .catch(() => loadGlobalErrorMessages(theme().site.locale))
      .catch(() => ({}) as AbstractIntlMessages)
      .then((loaded) => {
        if (!cancelled) {
          setMessages(loaded)
        }
      })

    return () => {
      cancelled = true
    }
  }, [locale])

  useEffect(() => {
    reportGlobalError(error, pathname, locale)
  }, [error, pathname, locale])

  return {
    locale,
    messages,
    errorTitle:
      (messages && getClientErrorTitle(messages)) ?? theme().site.title,
  }
}
